import { faChevronRight, faEnvelope, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useEffect, useState } from 'react';
import { cdnUrl, loginHubUrl } from '../constants/generic';
import { forgotPassword, getGtprCredentials, setGlobalsManager } from '../services/config';
import { initSite } from 'services/cms';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { showError, showSuccess } from 'utils/utils';
import { ToastContainer } from 'react-toastify';
import { initCSSVars } from '../utils/site';
import { GlobalsConfig } from '../services/config';

interface CognitoLoginPageProps {
  globals?: GlobalsConfig;
}
const forgotPasswordEmailRegex = /^$|^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const CognitoLoginPage: FC<CognitoLoginPageProps> = ({ globals }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSiteLoading, setIsSiteLoading] = useState(true);
  const [initSiteData, setInitSiteData] = useState<any>(null);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const normalizedForgotPasswordEmail = forgotPasswordEmail.trim();
  const isForgotPasswordEmailEmpty = !normalizedForgotPasswordEmail;
  const isForgotPasswordEmailValid = forgotPasswordEmailRegex.test(normalizedForgotPasswordEmail);

  useEffect(() => {
    (async () => {
      const initSiteData = await initSite();
      setInitSiteData(initSiteData);
      if (initSiteData) {
        initCSSVars(initSiteData as any);
      }
      setIsSiteLoading(false);
    })();
  }, []);
  const defaultLogoSrc = require('../assets/img/bepro-logo.png');
  const siteLogoUrl = initSiteData?.other?.logoUrl || defaultLogoSrc;
  const logoSrc =
    siteLogoUrl.startsWith('http') || siteLogoUrl.includes('/static/media/')
      ? siteLogoUrl
      : cdnUrl + siteLogoUrl;

  if (isSiteLoading) {
    return null;
  }

  const redirectToCognitoLogin = async (isManager: boolean) => {
    if (globals?.useBeproCognito) {
      const gtprCred = await getGtprCredentials();
      if (!gtprCred) {
        return;
      }

      const APP_ID = isManager ? globals.managerAppId : globals.appId;
      const REDIRECT_URI = process.env.REACT_APP_DEBUG ? 'http://localhost:3003' : gtprCred.redirectUri;

      const { verifier, challenge } = await generatePKCE();
      const pkceState = generateState();
      sessionStorage.setItem(`userPkce_verifier`, verifier);
      sessionStorage.setItem(`userPkce_state`, pkceState);

      const url = new URL(loginHubUrl);
      url.searchParams.set('app_id', APP_ID);
      url.searchParams.set('state', pkceState);
      url.searchParams.set('code_challenge', challenge);
      url.searchParams.set('code_challenge_method', 'S256');
      url.searchParams.set('redirect_uri', REDIRECT_URI);

      setGlobalsManager(isManager);
      window.location.href = url.toString();
      return;
    }
    const gtprCred = await getGtprCredentials();
    if (!gtprCred) {
      return;
    }
    setGlobalsManager(isManager);
    const loginUrl =
      `${isManager ? gtprCred.managerCognitoDomain : gtprCred.cognitoDomain}/login` +
      `?response_type=code` +
      `&client_id=${encodeURIComponent(isManager ? gtprCred.managerClientId : gtprCred.clientId)}` +
      `&redirect_uri=${process.env.REACT_APP_DEBUG ? encodeURIComponent('http://localhost:3003') : encodeURIComponent(gtprCred.redirectUri)}`;

    window.location.href = loginUrl;
  };

  const redirectToMicrosoft = async () => {
    if (!globals?.useAzureAD) {
      return;
    }
    const gtprCred = await getGtprCredentials();
    if (!gtprCred) {
      return;
    }

    const { verifier, challenge } = await generatePKCE();
    const pkceState = generateState();
    sessionStorage.setItem(`userPkce_verifier`, verifier);
    sessionStorage.setItem(`userPkce_state`, pkceState);

    const redirectUri = process.env.REACT_APP_DEBUG ? 'http://localhost:3003' : gtprCred.redirectUri;

    const url = new URL(`${gtprCred.cognitoDomain}/oauth2/authorize`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', gtprCred.clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('scope', 'openid');
    url.searchParams.set('identity_provider', 'issta-azure-identity-provider');
    url.searchParams.set('state', pkceState);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');

    setGlobalsManager(false);
    window.location.href = url.toString();
  };

  const handleForgotPassword = async () => {
    if (isForgotPasswordEmailEmpty || !isForgotPasswordEmailValid) {
      return;
    }

    setIsLoading(true);
    const res = await forgotPassword(normalizedForgotPasswordEmail);
    setIsLoading(false);
    if (res?.success) {
      showSuccess('Password reset email sent');
    } else {
      showError('Failed to send password reset email');
    }
  };
  return (
    <div className="main-login-page">
      <div className="header" style={{ height: 46 }}>
        <div className="header-logo-wr">
          <img className='header-logo' src={logoSrc} alt="logo" />
        </div>
      </div>
      <div className="main-login">
        <div className="main-login-card">
          <div className="main-login-logo">
            <img src={logoSrc} alt="logo" />
          </div>
          {!isForgotPasswordOpen && (
            <>
              <h1 className="main-login-title">Sign in to your account</h1>
              {!globals?.useAzureAD && (
                <button className="main-login-option main-login-option-active" type="button" onClick={() => redirectToCognitoLogin(false)}>
                  <div className="main-login-option-icon">
                    <FontAwesomeIcon icon={faUser as any} />
                  </div>
                  <div className="main-login-option-content">
                    <div className="main-login-option-title">Login as User</div>
                    <div className="main-login-option-description">Agents, branch staff</div>
                  </div>
                  <FontAwesomeIcon className="main-login-option-arrow" icon={faChevronRight as any} />
                </button>
              )}
              {globals?.useAzureAD && (
                <button
                  className="main-login-option main-login-option-azure"
                  type="button"
                  onClick={() => redirectToMicrosoft()}
                >
                  <div className="main-login-option-icon main-login-option-icon-azure">
                    <img src={require('../assets/img/microsoft-logo.png')} alt="Microsoft" />
                  </div>
                  <div className="main-login-option-content">
                    <div className="main-login-option-title">SSO</div>
                    <div className="main-login-option-description">Sign in with Microsoft Azure AD</div>
                  </div>
                  <FontAwesomeIcon className="main-login-option-arrow" icon={faChevronRight as any} />
                </button>
              )}
              <div
                className='btn btn-lnk'
                onClick={() => setIsForgotPasswordOpen(true)}
              >
                Forgot your password?
              </div>
              <div className="main-login-manager-link" onClick={() => redirectToCognitoLogin(true)}>
                A
              </div>
            </>
          )}
          {isForgotPasswordOpen && (
            <>
              <h1 className="main-login-title">Forgot password</h1>
              <div className="mt-20 mb-10">Enter your email to reset your password</div>
              <FormGroup
                onChange={(email) => setForgotPasswordEmail(email)}
                value={forgotPasswordEmail}
                label="Email"
                name="forgotPasswordEmail"
                movablePlaceholder='Email'
                movablePlaceholderIcon={faEnvelope as any}
              />
              <Button
                title="Reset password"
                className="btn btn-primary w100p btn-st mt-10"
                onClick={handleForgotPassword}
                disabled={isForgotPasswordEmailEmpty || !isForgotPasswordEmailValid || isLoading}
              />
              <div
                className='btn btn-lnk mt-10'
                onClick={() => {
                  setIsForgotPasswordOpen(false);
                  setForgotPasswordEmail('');
                }}
              >
                Close forgot password form
              </div>
            </>
          )}
        </div>
      </div>
      <div className="footer-wrapper">
        <div className="footer">
          <div className="col-md-3 col-6">
            <img className="footer-logo" src={logoSrc} alt="Logo" />
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};


const base64UrlEncode = (input: Uint8Array | ArrayBuffer) => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generatePKCE = async () => {
  const verifierBytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64UrlEncode(verifierBytes);
  const challengeBytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64UrlEncode(challengeBytes) };
};

const generateState = () => {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(16)));
};

export default CognitoLoginPage;
